// `/admin/system`, croqui `screens/admin/sistema.tsx`. Ordem do croqui: o aviso das
// filas paradas, a faixa de quatro números, o painel "Filas" e a latência por rota.
//
// Quem tem `useQuery`/`useMutation` é a rota; os componentes de `features/admin-system`
// recebem dado por prop.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  adminSystemKeys,
  adminsQueryOptions,
  jobsQueryOptions,
  type MetricsWindow,
  metricsQueryOptions,
  runJob,
} from '../api/queries/admin-system.ts';
import { ConfirmDialog } from '../components/app/confirm-dialog.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { adminNameMap } from '../features/admin-international-withdrawals/rules.ts';
import {
  CRYPTO_SETTINGS_ANCHOR,
  CryptoWithdrawalsSettingsPanel,
} from '../features/admin-system/crypto-withdrawals-settings.tsx';
import { JobsPanel } from '../features/admin-system/jobs-panel.tsx';
import { LatencyPanel, MetricsStats } from '../features/admin-system/metrics-panel.tsx';
import { isStepUpCancelled, mergeQueues, stalledQueues } from '../features/admin-system/model.ts';

function AdminSystem() {
  const queryClient = useQueryClient();
  const jobs = useQuery(jobsQueryOptions());
  const metrics = useQuery(metricsQueryOptions());
  // Só pra escrever o nome em "mudado por" das configurações de USDT (o audit devolve o id).
  const admins = useQuery(adminsQueryOptions());

  const [metricsWindow, setMetricsWindow] = useState<MetricsWindow>('5m');
  const [toRun, setToRun] = useState<string | undefined>(undefined);
  const [queued, setQueued] = useState<{ name: string; jobId: string } | undefined>(undefined);

  const run = useMutation({
    mutationFn: async (name: string) => ({ name, result: await runJob(name) }),
    onSuccess: ({ name, result }) => {
      setToRun(undefined);
      setQueued({ name, jobId: result.job_id });
      void queryClient.invalidateQueries({ queryKey: adminSystemKeys.system });
    },
    onError: (error) => {
      // Step-up cancelado é desistência do operador, não falha: fecha e não pinta erro.
      if (isStepUpCancelled(error)) {
        setToRun(undefined);
      }
    },
  });

  const stalled = stalledQueues(mergeQueues(jobs.data ?? []));

  return (
    <>
      <PageHeader
        title="Jobs e métricas"
        sub="Filas pg-boss, latência da API e saúde do outbox"
        path="/admin/v1/system"
      />

      {stalled.length > 0 && (
        <Notice
          tone="bad"
          icon="alert"
          title={
            stalled.length === 1
              ? '1 fila com trabalho parado ou atrasado.'
              : `${stalled.length} filas com trabalho parado ou atrasado.`
          }
        >
          {stalled.map((queue) => queue.name).join(', ')}, veja a tabela abaixo.
        </Notice>
      )}

      <MetricsStats metrics={metrics.data} />

      <JobsPanel
        queues={jobs.data ?? []}
        isLoading={jobs.isPending}
        // Erro só quando não há lista: com o polling de 30 s um flake no refetch de fundo
        // trocaria a tabela carregada por uma tela de falha.
        error={jobs.data === undefined ? jobs.error : undefined}
        onRetry={() => {
          void jobs.refetch();
        }}
        onRun={(name) => {
          run.reset();
          setQueued(undefined);
          setToRun(name);
        }}
        {...(run.isPending && toRun !== undefined ? { runningJob: toRun } : {})}
        {...(queued === undefined ? {} : { queued })}
        {...(metrics.data === undefined
          ? {}
          : { failedIn10m: metrics.data.queues.jobs_failed_10m })}
        {...(jobs.dataUpdatedAt === 0 ? {} : { updatedAt: jobs.dataUpdatedAt })}
      />

      <LatencyPanel
        metrics={metrics.data}
        isLoading={metrics.isPending}
        error={metrics.data === undefined ? metrics.error : undefined}
        onRetry={() => {
          void metrics.refetch();
        }}
        window={metricsWindow}
        onWindowChange={setMetricsWindow}
        {...(metrics.dataUpdatedAt === 0 ? {} : { updatedAt: metrics.dataUpdatedAt })}
      />

      {/* WEB-135: sem croqui. A âncora é o destino de "Abrir configurações" da fila de USDT. */}
      <div id={CRYPTO_SETTINGS_ANCHOR} className="scroll-mt-4">
        <Panel
          title="Saque internacional em USDT"
          sub="Taxa, limites e pausa do saque pago à mão por um admin"
        >
          <CryptoWithdrawalsSettingsPanel adminNames={adminNameMap(admins.data)} />
        </Panel>
      </div>

      <ConfirmDialog
        open={toRun !== undefined}
        title="Rodar o job agora?"
        description={
          toRun === undefined
            ? ''
            : `O cron "${toRun}" entra na fila imediatamente. Se já houver uma execução enfileirada, a API recusa.`
        }
        confirmLabel="Rodar agora"
        isPending={run.isPending}
        {...(run.error === null || isStepUpCancelled(run.error) ? {} : { error: run.error })}
        onConfirm={() => {
          if (toRun !== undefined) {
            run.mutate(toRun);
          }
        }}
        onOpenChange={(open) => {
          if (!open) {
            setToRun(undefined);
          }
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/admin/system/')({ component: AdminSystem });
